/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState } from "react";
import dayjs from "dayjs";
import { Row, Col } from "react-awesome-styled-grid";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { When } from "react-if";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  FlexContainer,
  ModalConfirm,
  Navigation,
  Popover,
  Spacer,
  SVGIcon,
  Text,
  theme,
} from "@nordcloud/gnui";
import {
  UpdateWorkflowInput,
  useGetWorkflowQuery,
  useUpdateWorkflowDetailMutation,
} from "~/generated/graphql";
import {
  BreadcrumbsBox,
  ErrorMessage,
  GlobalSpinner,
  UserErrorMessageHTML,
} from "~/components";
import { BrickLoader } from "~/components/Loaders/BrickLoader";
import { LoaderWrap } from "~/components/Utils";
import { dateFormat } from "~/constants";
import { useUuidValidator } from "~/hooks";
import { ROUTES } from "~/routing/routes";
import { ACTIONS, Can } from "~/services/auth";
import { showError, showSuccess } from "~/services/toast";
import { generateActionSuccessText, isEmpty, isNotNil } from "~/tools";
import { Page404 } from "~/views/404";
import { DetailsTitle } from "~/views/environments/components/DetailsTitle";
import {
  useActivateWorkflowAction,
  useCloneWorkflowAction,
  useExecuteWorkflowAction,
  useRemoveWorkflowAction,
} from "../hooks";
import { mapWorkflowProps } from "../WorkflowPropsMapping";
import { CloneWorkflowSidebar } from "./CloneWorkflowSidebar";
import { WorkflowDetailsInlineContacts } from "./WorkflowDetailsInlineContacts";
import { WorkflowDetailsInlineText } from "./WorkflowDetailsInlineText";
import { WorkflowDetailsTabs } from "./WorkflowDetailsTabs";

type RouteParams = {
  workflowId: string;
};

export function WorkflowDetailsPage() {
  const { workflowId = "" } = useParams<RouteParams>();
  useUuidValidator(workflowId);
  const [userError, setUserError] = useState<JSX.Element | undefined>();

  const navigate = useNavigate();

  const {
    data,
    loading,
    error: loadError,
  } = useGetWorkflowQuery({
    variables: { id: workflowId },
  });

  const executeAction = useExecuteWorkflowAction();
  const activateAction = useActivateWorkflowAction(setUserError);
  const removeAction = useRemoveWorkflowAction(() =>
    navigate(ROUTES.workflows.index)
  );

  const cloneAction = useCloneWorkflowAction(setUserError, (id) =>
    navigate(`${ROUTES.workflows.index}/${id}`)
  );

  const [updateWorkflowDetail] = useUpdateWorkflowDetailMutation();

  const onEditSubmit = async (input: UpdateWorkflowInput) => {
    try {
      await updateWorkflowDetail({ variables: { id: workflowId, input } });
      showSuccess(
        generateActionSuccessText(getSuccessText(input))()("updated")()
      );
    } catch {
      showError("Could not update workflow");
    }
  };

  const workflow = data?.workflow;
  const isWorkflowActive = workflow?.active;
  const activeName = isWorkflowActive
    ? "Deactivate workflow"
    : "Activate workflow";
  const activeIconName = isWorkflowActive ? "pause" : "play";

  const tags = [
    {
      color: isWorkflowActive ? "notification" : "warning",
      text: isWorkflowActive ? "Active" : "Deactivated",
    },
  ];

  const breadcrumbLabels = workflow
    ? [{ value: workflow.id, label: workflow.name }]
    : undefined;

  const createdAt = (date?: string | null) =>
    dayjs(date).format(dateFormat.dayShortMonthYear);

  const createdTime = createdAt(workflow?.createdAt);

  const executing =
    executeAction.loading ||
    activateAction.loading ||
    removeAction.loading ||
    cloneAction.loading;

  const showNotFoundError = loadError?.graphQLErrors.some(
    (err) => err.extensions.code === "NOT_FOUND"
  );

  if (showNotFoundError) {
    return <Page404 />;
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <LoaderWrap
        loading={loading}
        Component={BrickLoader}
        viewBox="0 0 10 4"
        backgroundColor={theme.color.background.ui01}
      >
        <GlobalSpinner loading={executing} />
        <ErrorMessage error={loadError} />
        <UserErrorMessageHTML
          html={userError}
          onClick={() => setUserError(undefined)}
        />
        <Row>
          <Col>
            <BreadcrumbsBox
              title={workflow?.name}
              tags={tags}
              labels={breadcrumbLabels}
            >
              <Can action={ACTIONS.updateWorkflow}>
                <FlexContainer justifyContent="flex-end">
                  <Popover
                    alignRight
                    trigger={
                      <Button
                        severity="low"
                        icon="menu"
                        size="md"
                        data-testid="workflow-popover-trigger"
                      />
                    }
                  >
                    <Navigation.Container popoverMenu>
                      <Navigation.Item
                        css={{ cursor: "pointer" }}
                        onClick={() =>
                          workflow && executeAction.onClick(workflow)
                        }
                      >
                        <SVGIcon name="thunder" />
                        <Text size="md" tag="span">
                          Force Run
                        </Text>
                      </Navigation.Item>
                      <Navigation.Item
                        css={{ cursor: "pointer" }}
                        onClick={() =>
                          workflow && activateAction.onClick(workflow)
                        }
                      >
                        <SVGIcon name={activeIconName} />
                        <Text size="md" tag="span">
                          {activeName}
                        </Text>
                      </Navigation.Item>
                      <Navigation.Item
                        css={{ cursor: "pointer" }}
                        onClick={() =>
                          workflow && cloneAction.onClick(workflow)
                        }
                      >
                        <SVGIcon name="copy" />
                        <Text size="md" tag="span">
                          Clone Workflow
                        </Text>
                      </Navigation.Item>
                      <Navigation.Item
                        css={{ cursor: "pointer" }}
                        onClick={() =>
                          workflow && removeAction.onClick(workflow)
                        }
                      >
                        <SVGIcon name="trash" />
                        <Text size="md" tag="span">
                          Delete
                        </Text>
                      </Navigation.Item>
                    </Navigation.Container>
                  </Popover>
                </FlexContainer>
              </Can>
              <Spacer height="spacing04" />
            </BreadcrumbsBox>
            <Box spacing="spacing04">
              <Row>
                <Col>
                  <FlexContainer direction="column" alignItems="start">
                    <Text isTitle weight="medium">
                      Workflow Details
                    </Text>
                    <FlexContainer alignItems="center">
                      <DetailsTitle>Workflow Name:</DetailsTitle>
                      <WorkflowDetailsInlineText
                        initValue={workflow?.name}
                        property="name"
                        onSubmit={onEditSubmit}
                      />
                    </FlexContainer>
                    <Spacer height={theme.spacing.spacing02} />
                    <FlexContainer alignItems="center">
                      <DetailsTitle>Contact Person:</DetailsTitle>
                      <When condition={isEmpty(workflow?.contactPersons ?? [])}>
                        Contact person missing
                      </When>
                      <WorkflowDetailsInlineContacts
                        initValues={workflow?.contactPersons ?? []}
                        property="contactPersonIds"
                        onSubmit={onEditSubmit}
                      />
                    </FlexContainer>
                    <Spacer height={theme.spacing.spacing02} />
                    <FlexContainer alignItems="center">
                      <DetailsTitle>Workflow Created:</DetailsTitle>
                      {createdTime}
                    </FlexContainer>
                    <Spacer height={theme.spacing.spacing02} />
                    <FlexContainer alignItems="center">
                      <DetailsTitle>Description:</DetailsTitle>
                      <WorkflowDetailsInlineText
                        initValue={workflow?.description}
                        property="description"
                        required={false}
                        onSubmit={onEditSubmit}
                      />
                    </FlexContainer>
                  </FlexContainer>
                </Col>
              </Row>
            </Box>
          </Col>
        </Row>
        {isNotNil(workflow) && (
          <WorkflowDetailsTabs workflow={mapWorkflowProps(workflow)} />
        )}
        <ModalConfirm {...executeAction.modalProps}>
          {executeAction.contentText}
        </ModalConfirm>
        <ModalConfirm {...activateAction.modalProps}>
          {activateAction.contentText}
        </ModalConfirm>
        <ModalConfirm {...removeAction.modalProps}>
          {removeAction.contentText}
        </ModalConfirm>
        <CloneWorkflowSidebar {...cloneAction.modalProps} />
      </LoaderWrap>
    </DndProvider>
  );
}

function getSuccessText(input: UpdateWorkflowInput): string {
  if (Object.hasOwn(input, "name")) {
    return "workflow name";
  }

  if (Object.hasOwn(input, "description")) {
    return "workflow description";
  }

  if (Object.hasOwn(input, "contactPersonIds")) {
    return "workflow contacts";
  }

  return "";
}
